import './index.less';

import { Button, DatePicker, Form, Input, InputNumber, message, Modal, Select } from 'antd';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  createManualPaymentThunk,
  createSmeManualPaymentThunk,
  getPaymentHistoryThunk,
} from '../../../services/paymentService/paymentThunk';
import { numberWithCommas } from '../../../helpers/number';
import { AppDispatch, RootState } from '../../../store/store';
import { getTransactionHistoryThunk } from '../../../services/transactionHistoryService/transactionHistoryThunk';
import { getAccountDetailsThunk } from '../../../services/collectionService/collectionThunk';
import { roundToDecimal } from '../../../utils/number.util';

const InsertPaymentModal: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { accountEnquiry } = useSelector((state: RootState) => state.collection);
  const { currentUser } = useSelector((state: RootState) => state.user);

  const getLateChargesAmount = () =>
    Number(accountEnquiry?.data?.accountDetails?.accountCharges?.latePaymentCharge ?? 0);

  const getOtherChargesAmount = () =>
    Number(accountEnquiry?.data?.accountDetails?.totalCharges) - getLateChargesAmount();

  const showModal = () => {
    setIsModalOpen(true);
  };

  const onFinish = async () => {
    await form.validateFields().then(async (values) => {
      if (currentUser?.data?.username == null) {
        return message.error('Current user not found!');
      }

      if (!accountEnquiry.data?.accountDetails?.id) {
        return message.error('Account details not found!');
      }

      setLoading(true);
      if (accountEnquiry?.data?.accountDetails?.smeApplicationId) {
        await dispatch(
          createSmeManualPaymentThunk({
            accountDetailsId: accountEnquiry.data.accountDetails.id,
            amount: values.amount,
            channel: values.channel,
            createdBy: currentUser?.data?.username,
            paymentDate: values.paymentDate,
            bankName: values.bank,
            referenceNo: values.referenceNo,
            loanType: 'SME',
            paymentIntent: {
              instalmentAmount: values.instalmentAmount,
              chargesAmount: values.lateChargesAmount + values.otherChargesAmount,
              lateChargesAmount: values.lateChargesAmount,
              otherChargesAmount: values.otherChargesAmount,
            },
          })
        )
          .unwrap()
          .then(() => {
            message.success('Payment added successfully');
            handleCancel();
            dispatch(
              getAccountDetailsThunk({
                agreementNo: accountEnquiry?.data?.agreementDetails?.agreementNo,
              })
            );
          })
          .catch((error) => {
            message.error(error.message || 'Failed to insert payment');
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        if (accountEnquiry.data?.agreementDetails?.id == null) {
          return message.error('Agreement not found!');
        }

        dispatch(
          createManualPaymentThunk({
            agreementId: accountEnquiry.data.agreementDetails.id,
            amount: values.amount,
            chargesAmount: values.lateChargesAmount + values.otherChargesAmount,
            instalmentAmount: values.instalmentAmount,
            lateChargesAmount: values.lateChargesAmount,
            otherChargesAmount: values.otherChargesAmount,
            channel: values.channel,
            createdBy: currentUser.data.username,
            paymentDate: values.paymentDate,
            bank: values.bank,
            tranId: values.referenceNo,
            referenceNo: values.referenceNo,
          })
        )
          .unwrap()
          .then((res) => {
            message.success('Request sent successfully');
            dispatch(
              getTransactionHistoryThunk({
                agreementId: accountEnquiry.data?.agreementDetails?.id,
              })
            );
            dispatch(
              getPaymentHistoryThunk({
                agreementId: accountEnquiry.data?.accountDetails?.agreementId,
              })
            );
            handleCancel();
          })
          .catch((error) => {
            message.error(error.message || 'Failed to insert payment');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setLoading(false);
    form.resetFields();
  };

  const handleAmountValuesChange = () => {
    const instalment = form.getFieldValue('instalmentAmount') ?? 0;
    const late = form.getFieldValue('lateChargesAmount') ?? 0;
    const other = form.getFieldValue('otherChargesAmount') ?? 0;
    form.setFieldsValue({ amount: roundToDecimal(instalment + late + other, 2) });
  };

  return (
    <>
      <Button onClick={showModal}>Insert Payment</Button>
      <Modal
        centered
        title="Insert Payment"
        open={isModalOpen}
        onOk={onFinish}
        onCancel={handleCancel}
        width={360}
        destroyOnClose
        okText="Insert Payment"
        confirmLoading={loading}
      >
        <Form
          form={form}
          name="insertPayment"
          layout="vertical"
          scrollToFirstError
          requiredMark={false}
          onValuesChange={handleAmountValuesChange}
        >
          <Form.Item
            name="amount"
            label="Transaction Amount"
            rules={[
              {
                required: true,
                message: 'Transaction Amount is required',
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} controls={false} disabled />
          </Form.Item>

          <Form.Item
            name="instalmentAmount"
            label="Instalment Amount"
            rules={[
              {
                required: true,
                message: 'Instalment Amount is required',
              },
              {
                validator: (_: any, value: number) => {
                  if (value < 0) {
                    return Promise.reject(new Error('Amount cannot be negative.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} controls={false} />
          </Form.Item>

          <Form.Item
            name="lateChargesAmount"
            label="Late Charges Amount"
            extra={`Current Late Payment Charges: RM${numberWithCommas(getLateChargesAmount())}`}
            rules={[
              {
                required: true,
                message: 'Late Payment Charges Amount is required',
              },
              {
                validator: (_: any, value: number) => {
                  const currentLateCharges = getLateChargesAmount();
                  if (currentLateCharges < value) {
                    return Promise.reject(
                      new Error('Amount cannot larger than Current Late Charges Amount.')
                    );
                  }
                  if (value < 0) {
                    return Promise.reject(new Error('Amount cannot be negative.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} controls={false} />
          </Form.Item>

          <Form.Item
            name="otherChargesAmount"
            label="Other Charges Amount"
            extra={`Current Other Charges: RM${numberWithCommas(getOtherChargesAmount())}`}
            rules={[
              {
                required: true,
                message: 'Other Charges Amount is required',
              },
              {
                validator: (_: any, value: number) => {
                  const currentOtherCharges = getOtherChargesAmount();
                  if (currentOtherCharges < value) {
                    return Promise.reject(
                      new Error('Amount cannot larger than Current Other Charges Amount.')
                    );
                  }
                  if (value < 0) {
                    return Promise.reject(new Error('Amount cannot be negative.'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber style={{ width: '100%' }} controls={false} />
          </Form.Item>
          <Form.Item
            name="channel"
            label="Channel"
            rules={[{ required: true, message: 'Payment Channel is required' }]}
          >
            <Select
              placeholder="Please Select"
              allowClear
              options={[
                { value: 'RAZER_PAY', label: 'Razer Pay' },
                { value: 'JOMPAY', label: 'JomPay' },
                { value: 'EWALLET', label: 'E-Wallet' },
                { value: 'DIRECT_DEBIT', label: 'Direct Debit' },
                { value: 'RHB_5590', label: 'RHB 5590' },
                { value: 'COMMISSION', label: 'Commission' },
                { value: 'SALARY', label: 'Salary' },
                { value: 'REFINANCE', label: 'Refinance' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="paymentDate"
            label="Payment Date"
            validateFirst={true}
            rules={[
              {
                required: true,
                message: 'Payment date is required',
              },
            ]}
          >
            <DatePicker placeholder="Select Date" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="bank"
            label="Bank"
            rules={[{ required: true, message: 'Bank is required' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="referenceNo"
            label="Reference No"
            rules={[{ required: true, message: 'Reference No. is required' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default InsertPaymentModal;
